import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { type HistoricalBudgetMixResponse } from "../../../budget-mix-card-container/card-container";

import {
  handleExportHistoricalBudgetMix,
  handleExportHistoricalScenario,
} from "./utils";

type Props = {
  historicalData: HistoricalBudgetMixResponse;
};

export function ExportHistoricalBudgetMix({ historicalData }: Props) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { kpis } = useClientKpis();
  const { selectedCycle } = useSelectedCycle();

  return (
    <Menu as="div" className="relative z-[9]">
      {({ open: isOpen }) => (
        <>
          <GenericTooltip content="Export" noInfoCircle>
            <MenuButton
              className={clsx("rounded-md h-6 w-6 flex-center text-sm", {
                "text-cosmic hover:bg-cosmic-4": !isOpen,
                "text-white bg-cosmic": isOpen,
              })}
            >
              <i className="fa-regular fa-download" />
            </MenuButton>
          </GenericTooltip>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <MenuItems
              className={clsx(
                "absolute rounded-md bg-v2-primary py-1 text-sm elevation-1 w-[320px]",
                "overflow-hidden right-0",
              )}
            >
              <div className="font-bold px-3 py-2 text-interstellar+2">
                Export as
              </div>
              <MenuItem
                as="div"
                onClick={() => {
                  trackMMMEvent({
                    component:
                      ExploreBudgetsTrackedComponent.EXPORT_HISTORICAL_BUDGET_MIX_SELECTOR,
                    action: "onClick",
                    actionInfo: "Export historical budget mix summary",
                  });

                  handleExportHistoricalBudgetMix(
                    historicalData,
                    kpis,
                    user.currencies.currentCurrency,
                  );
                }}
              >
                {({ focus }) => (
                  <div
                    className={clsx("px-3 py-2 hover:cursor-pointer", {
                      "bg-v2-secondary": focus,
                    })}
                  >
                    <div className="flex items-center space-x-2">
                      <i className="fa-regular fa-file-csv text-cosmic" />
                      <div>Total period budget, KPIs, and weekly spend</div>
                    </div>
                    <div className="text-gray+1 text-xxs mt-1">
                      Includes total period budget, weekly spend amounts, and
                      the forecasted KPIs shown in this page
                    </div>
                  </div>
                )}
              </MenuItem>
              <MenuItem
                as="div"
                onClick={() => {
                  trackMMMEvent({
                    component:
                      ExploreBudgetsTrackedComponent.EXPORT_HISTORICAL_BUDGET_MIX_SELECTOR,
                    action: "onClick",
                    actionInfo: "Export historical budget mix spend",
                  });

                  handleExportHistoricalScenario(
                    historicalData,
                    selectedCycle.date_range.start_date,
                    user.currencies.currentCurrency,
                  );
                }}
              >
                {({ focus }) => (
                  <div
                    className={clsx("px-3 py-2 hover:cursor-pointer", {
                      "bg-v2-secondary": focus,
                    })}
                  >
                    <div className="flex items-center gap-2">
                      <i className="fa-regular fa-file-csv text-cosmic" />
                      <div>Weekly spend sheet</div>
                    </div>
                    <div className="text-gray+1 text-xxs mt-1">
                      Modify and upload in the &#8216;Create Your Own
                      Budgets&#8216; tab to create a custom budget
                    </div>
                  </div>
                )}
              </MenuItem>
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
}
